import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from "react";

import type { AdminRole } from "@attendik/types/admin-schemas";

import { clearToken, getToken, setRefreshToken, setToken } from "../api/client.js";

export interface AdminIdentity {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  customer_id: string | null;
}

export interface AuthState {
  token: string | null;
  user: AdminIdentity | null;
  /** Persists the token (+ optional refresh token) and derives the identity. */
  login: (token: string, refreshToken?: string) => void;
  logout: () => void;
}

/**
 * Decodes the JWT payload only — signature verification is the server's job.
 * The admin token carries `{ sub, email, name, role, customer_id }`.
 */
export function decodeIdentity(token: string): AdminIdentity | null {
  try {
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment) {
      return null;
    }
    const json = atob(payloadSegment.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as {
      sub?: string;
      email?: string;
      name?: string;
      role?: string;
      customer_id?: string | null;
    };
    if (
      !claims.sub ||
      !claims.email ||
      !claims.name ||
      (claims.role !== "portal_admin" && claims.role !== "customer_admin")
    ) {
      return null;
    }
    return {
      id: claims.sub,
      email: claims.email,
      name: claims.name,
      role: claims.role,
      customer_id: claims.customer_id ?? null
    };
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setTokenState] = useState<string | null>(() => getToken());

  const value = useMemo<AuthState>(() => {
    return {
      token,
      user: token ? decodeIdentity(token) : null,
      login: (newToken: string, refreshToken?: string) => {
        setToken(newToken);
        if (refreshToken) {
          setRefreshToken(refreshToken);
        }
        setTokenState(newToken);
      },
      logout: () => {
        clearToken();
        setTokenState(null);
      }
    };
  }, [token]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}

export interface Scope {
  isPortalAdmin: boolean;
  isCustomerAdmin: boolean;
  customerId: string | null;
}

/**
 * Derives scope flags from the authenticated identity. Components branch on
 * these at render time (e.g. the Customers nav item shows only for portal
 * admins).
 */
export function useScope(): Scope {
  const { user } = useAuth();
  return {
    isPortalAdmin: user?.role === "portal_admin",
    isCustomerAdmin: user?.role === "customer_admin",
    customerId: user?.customer_id ?? null
  };
}
